import React, { ReactElement, useMemo, useState } from 'react';
import cx from 'classnames';
import * as Sentry from '@sentry/browser';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';
import { ChevronDown, Download, FileSpreadsheet, TriangleAlert } from 'lucide-react';
import { MembershipStatus, ProductEntity, ProductStatus, ReportEntity } from '@data/utils/types';
import { fetchProduct, fetchSprintProductReports } from '@data/api';
import * as queryKeys from '@data/constants/keys';
import { useListPmProducts } from '@data/hooks/products';
import { useListSprints } from '@data/hooks/sprints';
import { useListPmMembers } from '@data/hooks/membership';
import { useOrganizationId } from '@app/utils/hooks';
import { limitConcurrency } from '@app/utils/helpers/limitConcurrency';
import { ClearFilterButton } from '@app/components/core';
import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/ui/popover';
import { Label } from '@ui/components/ui/label';
import { Button } from '@ui/components/ui/button';
import SpreadsheetPreview from '@app/components/core/SpreadsheetPreview';
import MonthPicker from './MonthPicker';
import {
  DocType,
  ExportPeople,
  PROJECT_COLUMNS,
  SprintProductReports,
  TIMESHEET_COLUMNS,
  exportFilename,
  overlapsMonth,
  projectRows,
  rndSharesFor,
  timesheetRows,
  toSheetData,
} from './exportRows';

const ALL_STATUSES = [ProductStatus.active, ProductStatus.support, ProductStatus.archived, ProductStatus.deactivated];

// A month's reads are one per sprint per product, so the count follows the data: a busy month in a large
// organization is hundreds of requests, and firing them together exhausted staging's database connections
// and failed the export outright. Four at a time keeps the preview quick and the API standing.
const READS_AT_ONCE = 4;

// The documents are named by the file they produce, so what is picked and what lands in Downloads read the
// same. Not translated for the same reason the column headings are not: the claim is filed with these names.
const DOC_OPTIONS: { value: DocType; label: string }[] = [
  { value: 'projects', label: 'projects' },
  { value: 'timesheets', label: 'timesheets' },
];

/**
 * The Exports tab: the two spreadsheets behind Panenco's Belgian BV O&O claim.
 *
 * Every field comes from reads that already ship. Nothing here is invented and nothing is mocked — where
 * Phill does not hold a value (the national registry number, academic collaboration) the column is present
 * and empty, and the manager completes it by hand.
 */
const ExportsPanel = (): ReactElement => {
  const { t } = useTranslation();
  const organizationId = useOrganizationId() ?? '';

  // Guided: nothing is chosen at first, the month opens up once a document is picked, and the preview fills
  // in once both are set.
  const [docType, setDocType] = useState<DocType | null>(null);
  const [month, setMonth] = useState('');
  const [docOpen, setDocOpen] = useState(false);
  const ready = docType !== null && month !== '';

  const { data: sprintsData, isLoading: sprintsLoading } = useListSprints({ limit: 200 });
  const productParams = useMemo(() => ({ statuses: ALL_STATUSES }), []);
  const { data: productsData, isLoading: productsLoading } = useListPmProducts(organizationId, productParams);
  // Holiday and absence are not R&D. Excluding them here keeps them out of both documents at once, and out
  // of the report reads below, so a month of holiday costs no requests either.
  const products = useMemo(() => (productsData?.items ?? []).filter((p: ProductEntity) => p.billable), [productsData]);

  const sprintsInMonth = useMemo(
    () => (month ? (sprintsData?.items ?? []).filter(s => overlapsMonth(s, month)) : []),
    [sprintsData, month],
  );

  // One read per sprint and product: the only route that returns a product's reports without asking whose
  // they are. Nothing is asked for until a month narrows it to the sprints that can hold rows.
  const pairs = useMemo(
    () => sprintsInMonth.flatMap(sprint => products.map(product => ({ sprint, product }))),
    [sprintsInMonth, products],
  );
  // One queue for every read this panel makes, so the report reads and the team reads below share the
  // ceiling instead of each getting their own.
  const queue = useMemo(() => limitConcurrency(READS_AT_ONCE), []);
  const reportQueries = useQueries({
    queries: pairs.map(({ sprint, product }) => ({
      queryKey: [queryKeys.PM_SPRINT_PRODUCT_REPORTS, { sprintId: sprint.id, productId: product.id }],
      queryFn: () => queue(() => fetchSprintProductReports({ sprint, productId: product.id })),
      enabled: ready,
    })),
  });
  const reportsLoading = reportQueries.some(q => q.isLoading && q.fetchStatus !== 'idle');
  const reportsFailed = reportQueries.filter(q => q.isError);

  const reportStamps = reportQueries.map(q => q.dataUpdatedAt).join(',');
  const groups = useMemo<SprintProductReports[]>(
    () =>
      pairs.map(({ sprint, product }, i) => ({
        sprint,
        product,
        reports: (reportQueries[i]?.data?.items ?? []) as ReportEntity[],
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pairs, reportStamps],
  );

  // The member number lives on the membership, which the organization's member list already carries.
  const memberParams = useMemo(() => ({ userStatus: MembershipStatus.active }), []);
  const { data: membersData } = useListPmMembers(organizationId, memberParams, docType === 'timesheets');

  // The R&D share is per person per product, so it comes from the product itself — and only for the products
  // the timesheets actually name. A month with three products worked on costs three reads, not one per
  // product in the organization.
  const productsWithRows = useMemo(
    () => [...new Set(groups.filter(g => g.reports.length > 0).map(g => g.product.id))],
    [groups],
  );
  const teamQueries = useQueries({
    queries: productsWithRows.map(productId => ({
      queryKey: [queryKeys.PRODUCT, productId],
      queryFn: () => queue(() => fetchProduct(productId)),
      enabled: docType === 'timesheets',
    })),
  });
  const teamStamps = teamQueries.map(q => q.dataUpdatedAt).join(',');
  // Only the timesheets document issues these; a Projects export builds the same array with every query
  // disabled, and a disabled query keeps whatever error it cached, so both counts read off this.
  const teamReads = docType === 'timesheets' ? teamQueries : [];
  const teamsLoading = teamReads.some(q => q.isLoading && q.fetchStatus !== 'idle');
  const teamsFailed = teamReads.filter(q => q.isError);
  const teamReadCount = teamReads.length;

  // A read that failed contributes no rows, exactly as a sprint with nothing logged does, so an incomplete
  // file is indistinguishable from a quiet month. These documents back an O&O claim, so the export is held
  // back until every read has landed rather than letting a short one through.
  const failed = [...reportsFailed, ...teamsFailed];
  const retryFailed = (): void => {
    failed.forEach(query => {
      query.refetch().catch(() => {
        // The row stays in the failed list and keeps saying so, which is the whole report this needs.
      });
    });
  };

  const people = useMemo<ExportPeople>(() => {
    const memberNumbers = new Map<string, string>();
    (membersData?.items ?? []).forEach(m => {
      if (m.memberNumber) memberNumbers.set(m.id, m.memberNumber);
    });
    const rndShares = rndSharesFor(
      productsWithRows.map((productId, i) => ({ productId, team: teamQueries[i]?.data?.team ?? [] })),
    );

    return { memberNumbers, rndShares };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membersData, productsWithRows, teamStamps]);

  const columns = docType === 'timesheets' ? TIMESHEET_COLUMNS : PROJECT_COLUMNS;
  const rows = useMemo(() => {
    if (!ready) return [];

    return docType === 'timesheets' ? timesheetRows(groups, month, people) : projectRows(groups, month);
  }, [ready, docType, groups, month, people]);

  const loading = ready && (sprintsLoading || productsLoading || reportsLoading || teamsLoading);
  const filename = exportFilename(docType ?? 'projects', month);
  const sheetName = docType === 'timesheets' ? 'Timesheets' : 'Projecten';

  // Built from the arrays the preview rendered, so the file cannot say something the screen did not. The
  // workbook writer is imported here rather than at the top so its chunk is fetched by the managers who
  // export, not by everyone who opens Insights.
  const download = async (): Promise<void> => {
    try {
      const { default: writeXlsxFile } = await import('write-excel-file/browser');
      await writeXlsxFile(toSheetData(columns, rows), { sheet: sheetName }).toFile(filename);
      toast.success(t('exportDownloaded', { file: filename }));
    } catch (error) {
      // Never a silent failure: the manager is standing at a download that did not happen, and a writer that
      // throws on real data is something we want to hear about rather than only they do.
      Sentry.captureException(error);
      toast.error(t('exportFailed'));
    }
  };

  const docLabel = DOC_OPTIONS.find(o => o.value === docType)?.label ?? '';

  return (
    <div className="w-full min-w-0">
      {/* 1) the document, 2) the month. */}
      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div>
          <Label className="mb-1.5 block">{t('document')}</Label>
          <Popover open={docOpen} onOpenChange={setDocOpen}>
            <PopoverTrigger
              type="button"
              className="flex h-8 w-[12rem] items-center justify-between gap-2 rounded-lg border border-input bg-popover px-3 text-left text-sm text-foreground shadow-xs outline-none transition-colors hover:bg-muted/50 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
            >
              <span className={cx('min-w-0 flex-1 truncate', !docType && 'text-muted-foreground')}>
                {docLabel || t('selectDocument')}
              </span>
              <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[12rem] p-1">
              {DOC_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    setDocType(opt.value);
                    setDocOpen(false);
                  }}
                  className={cx(
                    'flex w-full items-center rounded-md px-2 py-1.5 text-sm text-foreground transition-colors',
                    docType === opt.value ? 'bg-muted font-medium' : 'hover:bg-muted',
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <Label className="mb-1.5 block">{t('month')}</Label>
          {/* Locked only until the flow has started: a month already chosen stays changeable even if the
              document is cleared afterwards, rather than greying out while still holding a value. */}
          <MonthPicker value={month} onChange={setMonth} disabled={!docType && !month} placeholder={t('selectMonth')} />
        </div>
        {(docType !== null || month !== '') && (
          <ClearFilterButton
            label={t('clearAll')}
            onClick={() => {
              setDocType(null);
              setMonth('');
            }}
          />
        )}
      </div>

      {/* 3) the preview — a placeholder holds the space until both are chosen, then the sheet rolls down. */}
      <div className="relative h-[clamp(420px,calc(100vh_-_293px),1000px)]">
        <div
          className={cx(
            'absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border px-6 text-center transition-opacity duration-300',
            ready && 'pointer-events-none opacity-0',
          )}
        >
          <FileSpreadsheet className="size-8 text-muted-foreground/50" aria-hidden />
          <p className="text-sm text-muted-foreground">{t('selectToPreview')}</p>
        </div>
        {/* `inset-0`, not `top-0`: bounded by its slot, so a truncated sheet's note cannot push the card over Export. */}
        <div
          className={cx(
            'absolute inset-0 grid transition-[grid-template-rows] duration-[400ms] ease-out',
            ready ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
          )}
        >
          <div className="min-h-0 overflow-hidden">
            <SpreadsheetPreview
              filename={filename}
              sheets={[{ name: sheetName, columns, rows }]}
              loading={loading}
              fill
            />
          </div>
        </div>
      </div>

      {/* 4) the download, once there is something to download. An empty month is not a file worth writing,
          and neither is one whose reads did not all arrive. */}
      <div className="mt-6 flex items-center gap-3">
        <Button disabled={!ready || loading || rows.length === 0 || failed.length > 0} onClick={download}>
          <Download className="size-4" />
          {t('actions.export')}
        </Button>
        {ready && !loading && failed.length > 0 && (
          <span className="flex items-center gap-2 text-sm text-destructive">
            <TriangleAlert className="size-4 shrink-0" aria-hidden />
            {t('exportReadsFailed', { failed: failed.length, total: pairs.length + teamReadCount })}
            <button type="button" onClick={retryFailed} className="font-medium underline underline-offset-2">
              {t('retry')}
            </button>
          </span>
        )}
        {ready && !loading && failed.length === 0 && rows.length === 0 && (
          <span className="text-sm text-muted-foreground">{t('nothingToExport')}</span>
        )}
      </div>
    </div>
  );
};

export default ExportsPanel;
